// ActiveCellContext.tsx
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface ActiveCellState {
  id: string | null;
  content: string;
}

interface ActiveCellContextType {
  activeCellId: string | null;
  activeCellContent: string;
  setActiveCell: (data: ActiveCellState) => void;
}

const ActiveCellContext = createContext<ActiveCellContextType | undefined>(undefined);

export const ActiveCellProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ActiveCellState>({ id: null, content: '' });

  // 1. Memoize the updater function to keep its reference stable
  const setActiveCell = useCallback((data: ActiveCellState) => {
    setState(data);
  }, []);

  // 2. Memoize the context value object
  // It only changes when the ID or Content actually changes
  const contextValue = useMemo(() => ({
    activeCellId: state.id,
    activeCellContent: state.content,
    setActiveCell
  }), [state.id, state.content, setActiveCell]);

  return (
    <ActiveCellContext.Provider value={contextValue}>
      {children}
    </ActiveCellContext.Provider>
  );
};

export const useActiveCell = () => {
  const context = useContext(ActiveCellContext);
  if (!context) {
    throw new Error('useActiveCell must be used within ActiveCellProvider');
  }
  return context;
};
